import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getMyVotes, submitVoteRequest } from "../../services/learningApi";
import { findCollectionSubtitlesByInfo } from "../../services/subtitleApi";
import "./Vote.css";

const capitalizeWords = (str) =>
  (str || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const VoteHome = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total: null, activeVotes: 0, invalidated: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const [movieQuery, setMovieQuery] = useState("");
  const [videoType, setVideoType] = useState("movie");
  const [season, setSeason] = useState("");
  const [episode, setEpisode] = useState("");

  const [results, setResults] = useState([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const resultsRef = useRef(null);

  const [submittingId, setSubmittingId] = useState(null);
  const [voteStatus, setVoteStatus] = useState({});

  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      try {
        const response = await getMyVotes({ page: 1, limit: 100 });
        if (cancelled) return;
        const data = response?.data;
        const items = data?.items || [];
        const activeVotes = items.reduce((sum, item) => sum + (item.activeVotes || 0), 0);
        const invalidated = items.filter((item) => item.status === "invalidated").length;
        setStats({
          total: data?.pagination?.total ?? items.length,
          activeVotes,
          invalidated,
        });
      } catch (err) {
        if (cancelled) return;
        setStats({ total: null, activeVotes: 0, invalidated: 0 });
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = async (page = 1) => {
    const name = movieQuery.trim();
    if (!name || searching) return;
    setSearching(true);
    setSearchError("");
    if (page === 1) {
      setResults([]);
      setResultTotal(0);
    }
    try {
      const response = await findCollectionSubtitlesByInfo(
        name,
        videoType === "series" ? season : null,
        videoType === "series" ? episode : null,
        videoType,
        page
      );
      const data = response?.data || {};
      const docs = data.docs || [];
      // کالکشن‌های UMLANG در اولویت نمایش قرار می‌گیرند
      const sorted = [...docs].sort(
        (a, b) =>
          (b.collectionType === "umlang" ? 1 : 0) -
          (a.collectionType === "umlang" ? 1 : 0)
      );
      setResults(sorted);
      setSearchPage(data.page ?? page);
      setResultTotal(data.totalDocs ?? docs.length);
      setPagination({
        total: data.totalDocs ?? docs.length,
        page: data.page ?? page,
        totalPages: data.totalPages ?? 1,
        hasNextPage: data.hasNextPage ?? false,
        hasPrevPage: data.hasPrevPage ?? false,
      });
      if (docs.length === 0) {
        setSearchError("no-results");
      }
    } catch (err) {
      console.error("search subtitles error:", err);
      setSearchError("خطا در ارتباط با سرور");
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(1);
  };

  const handlePageChange = (page) => {
    if (!pagination) return;
    if (page < 1 || page > pagination.totalPages || page === pagination.page) return;
    runSearch(page);
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const pageNumbers = () => {
    if (!pagination) return [];
    const current = pagination.page;
    const total = pagination.totalPages;
    const nums = new Set([1]);
    for (let p = current - 2; p <= current + 2; p++) {
      if (p >= 1 && p <= total) nums.add(p);
    }
    nums.add(total);
    return [...nums].sort((a, b) => a - b);
  };

  const handleVote = async (item) => {
    const collectionId = item?.collectionId;
    if (!collectionId || submittingId) return;
    setSubmittingId(collectionId);
    try {
      const result = await submitVoteRequest({
        collectionId,
        subtitleUploadId: item.subOneID,
      });
      setVoteStatus((prev) => ({
        ...prev,
        [collectionId]: result.alreadyExists
          ? {
              kind: "info",
              message:
                "برای این کالکشن دو زیرنویس قبلاً محتوای یادگیری استخراج شده است.",
            }
          : result.alreadyVoted
          ? {
              kind: "success",
              message:
                "درخواست شما قبلاً برای این کالکشن دو زیرنویس ثبت شده است.",
            }
          : {
              kind: "success",
              message:
                "درخواست استخراج لغات و اصطلاحات با موفقیت ثبت شد.",
            },
      }));
    } catch (err) {
      setVoteStatus((prev) => ({
        ...prev,
        [collectionId]: {
          kind: "danger",
          message:
            err?.message || "خطا در ثبت درخواست. لطفاً بعداً تلاش کنید.",
        },
      }));
    } finally {
      setSubmittingId(null);
    }
  };

  const hasUmlangResults = results.some((r) => r.collectionType === "umlang");

  return (
    <>
      {/* Hero */}
      <section className="vote-hero">
        <h2>درخواست استخراج لغات و اصطلاحات زیرنویس</h2>
        <p>
          با ثبت درخواست برای هر فیلم یا سریال، تیم UMLANG لغات و اصطلاحات مهم
          زیرنویس شماره ۱ را از کالکشن دو زیرنویس استخراج می‌کند. به‌محض
          تکمیل شدن درخواست‌ها، زیرنویس‌های پرطرفدار در اولویت پردازش قرار
          می‌گیرند و محتوای آموزشی آن‌ها در دسترس قرار می‌گیرد.
        </p>
        <div className="vote-hero-actions">
          <button
            className="vote-btn vote-btn-primary"
            onClick={() => navigate("/vote/MyVotes")}
          >
            <i className="fas fa-clipboard-list"></i>
            مشاهده درخواست‌های من
          </button>
          <button
            className="vote-btn"
            onClick={() => navigate("/vote/FAQs")}
          >
            <i className="fas fa-circle-question"></i>
            سوالات متداول
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="vote-stats">
        <div className="vote-stat-card">
          <div className="vote-stat-icon teal">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div>
            <div className="vote-stat-value">
              {statsLoading ? "…" : stats.total ?? "—"}
            </div>
            <div className="vote-stat-label">درخواست‌های من</div>
          </div>
        </div>
        <div className="vote-stat-card">
          <div className="vote-stat-icon purple">
            <i className="fas fa-thumbs-up"></i>
          </div>
          <div>
            <div className="vote-stat-value">
              {statsLoading ? "…" : stats.activeVotes}
            </div>
            <div className="vote-stat-label">رأی فعال ثبت‌شده</div>
          </div>
        </div>
        {resultTotal > 0 && (
          <div className="vote-stat-card">
            <div className="vote-stat-icon orange">
              <i className="fas fa-layer-group"></i>
            </div>
            <div>
              <div className="vote-stat-value">{resultTotal}</div>
              <div className="vote-stat-label">کالکشن یافت‌شده</div>
            </div>
          </div>
        )}
      </section>

      {/* Search */}
      <section className="vote-search-card">
        <h3 className="vote-section-title">
          <i className="fas fa-magnifying-glass"></i>
          جستجوی کالکشن دو زیرنویس
        </h3>

        <form onSubmit={handleSearchSubmit}>
          <div className="vote-search-row">
            <input
              className="vote-input"
              placeholder="نام فیلم یا سریال را وارد کنید…"
              value={movieQuery}
              onChange={(e) => setMovieQuery(e.target.value)}
            />
            <button
              type="submit"
              className="vote-btn vote-btn-primary"
              disabled={!movieQuery.trim() || searching}
            >
              {searching ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-magnifying-glass"></i>
              )}
              جستجو
            </button>
          </div>

          <div className="vote-type-toggle">
            <button
              type="button"
              className={`vote-type-btn ${videoType === "movie" ? "active" : ""}`}
              onClick={() => setVideoType("movie")}
            >
              <i className="fas fa-film"></i>
              فیلم
            </button>
            <button
              type="button"
              className={`vote-type-btn ${videoType === "series" ? "active" : ""}`}
              onClick={() => setVideoType("series")}
            >
              <i className="fas fa-tv"></i>
              سریال
            </button>
          </div>

          {videoType === "series" && (
            <div className="vote-search-fields">
              <label className="vote-field">
                <span>فصل</span>
                <input
                  className="vote-input"
                  type="number"
                  min="1"
                  placeholder="اختیاری"
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                />
              </label>
              <label className="vote-field">
                <span>قسمت</span>
                <input
                  className="vote-input"
                  type="number"
                  min="1"
                  placeholder="اختیاری"
                  value={episode}
                  onChange={(e) => setEpisode(e.target.value)}
                />
              </label>
            </div>
          )}
        </form>

        {(searchError || results.length > 0) && (
          <div className="vote-results" ref={resultsRef}>
            {searchError === "no-results" && (
              <div className="vote-result-empty">
                نتیجه‌ای با این مشخصات پیدا نشد.
                <small>
                  نام سریال را بدون شماره فصل و قسمت هم می‌توانید امتحان کنید.
                </small>
              </div>
            )}
            {searchError && searchError !== "no-results" && (
              <div className="vote-result-error">{searchError}</div>
            )}

            {hasUmlangResults && (
              <div className="vote-alert vote-alert-success">
                <i className="fas fa-circle-check"></i>
                <span>
                  کالکشن‌های دارای نشان UMLANG، نسخه رسمی هستند و پیشنهاد
                  می‌کنیم از بین آن‌ها انتخاب کنید.
                </span>
              </div>
            )}

            {results.map((item, idx) => {
              const isUmlang = item.collectionType === "umlang";
              const title =
                capitalizeWords(item.movieName) ||
                item.movieFileName ||
                "نامشخص";
              return (
                <div
                  className={`vote-movie-card ${isUmlang ? "umlang" : ""}`}
                  key={item.collectionId || idx}
                >
                  <div className="vote-movie-cover">
                    {item.coverUrl ? (
                      <img src={item.coverUrl} alt="" loading="lazy" />
                    ) : (
                      <i className="fas fa-film"></i>
                    )}
                  </div>
                  <div className="vote-movie-info">
                    <div className="vote-movie-title">
                      {title}
                      {item.videoType === "series" &&
                        (item.season != null || item.episode != null) && (
                          <span className="vote-movie-ep">
                            {item.season != null && `· فصل ${item.season}`}
                            {item.episode != null && ` قسمت ${item.episode}`}
                          </span>
                        )}
                    </div>
                    <div className="vote-movie-meta">
                      <span className="vote-chip teal">
                        <i className="fas fa-hashtag"></i>
                        {item.collectionId}
                      </span>
                      {item.videoType === "series" ? (
                        <span className="vote-chip gray">
                          <i className="fas fa-tv"></i>
                          سریال
                        </span>
                      ) : (
                        <span className="vote-chip gray">
                          <i className="fas fa-film"></i>
                          فیلم
                        </span>
                      )}
                      {item.langOne && (
                        <span className="vote-chip small vote-lang-chip">
                          <i className="fas fa-language"></i>
                          <span dir="ltr">
                            {item.langOne}
                            {item.langTwo ? ` → ${item.langTwo}` : ""}
                          </span>
                        </span>
                      )}
                      {isUmlang && (
                        <span className="vote-chip vote-badge-umlang">
                          <i className="fas fa-circle-check"></i>
                          UMLANG
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="vote-card-action">
                    {voteStatus[item.collectionId] ? (
                      <div
                        className={`vote-alert vote-alert-${
                          voteStatus[item.collectionId].kind
                        } vote-result-inline`}
                      >
                        <i
                          className={`fas ${
                            voteStatus[item.collectionId].kind === "danger"
                              ? "fa-circle-xmark"
                              : voteStatus[item.collectionId].kind === "info"
                              ? "fa-circle-info"
                              : "fa-circle-check"
                          }`}
                        ></i>
                        <span>{voteStatus[item.collectionId].message}</span>
                        <Link
                          to={`/vote/collections/${encodeURIComponent(
                            item.collectionId
                          )}`}
                          className="vote-result-link"
                        >
                          مشاهده جزئیات
                        </Link>
                      </div>
                    ) : (
                      <button
                        className="vote-btn vote-btn-sm vote-btn-primary"
                        disabled={!!submittingId}
                        onClick={() => handleVote(item)}
                      >
                        {submittingId === item.collectionId ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-hand-pointer"></i>
                        )}
                        {submittingId === item.collectionId
                          ? "در حال ثبت…"
                          : "ثبت درخواست"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {pagination && pagination.totalPages > 1 && (
              <div className="vote-pagination">
                <button
                  className="vote-page-btn"
                  disabled={!pagination.hasPrevPage || searching}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
                {pageNumbers().map((p, i, arr) => {
                  const showGap = i > 0 && p - arr[i - 1] > 1;
                  return (
                    <span key={p} className="vote-page-group">
                      {showGap && (
                        <span className="vote-page-gap">…</span>
                      )}
                      <button
                        className={`vote-page-btn ${
                          p === pagination.page ? "current" : ""
                        }`}
                        disabled={searching}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </span>
                  );
                })}
                <button
                  className="vote-page-btn"
                  disabled={!pagination.hasNextPage || searching}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <div className="vote-page-info">
                  صفحه {pagination.page} از {pagination.totalPages} —{" "}
                  {pagination.total} کالکشن
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* How it works */}
      <section>
        <h3 className="vote-section-title">
          <i className="fas fa-list-check"></i>
          این‌طور کار می‌کند
        </h3>
        <div className="vote-steps">
          <div className="vote-step">
            <div className="vote-step-num">۱</div>
            <h4>کالکشن دو زیرنویس را پیدا کنید</h4>
            <p>
              از طریق همین جستجو یا لینک‌های اشتراک‌گذاری‌شده توسط دیگران،
              صفحه فیلم یا سریال را باز کنید.
            </p>
          </div>
          <div className="vote-step">
            <div className="vote-step-num">۲</div>
            <h4>درخواست را ثبت کنید</h4>
            <p>
              با یک کلیک، درخواست استخراج لغات و اصطلاحات زیرنویس شماره ۱ را
              ثبت کنید.
            </p>
          </div>
          <div className="vote-step">
            <div className="vote-step-num">۳</div>
            <h4>جمع‌آوری رأی</h4>
            <p>
              هرچه کالکشن دو زیرنویسی رأی بیشتری بگیرد، در اولویت پردازش تیم
              قرار می‌گیرد.
            </p>
          </div>
          <div className="vote-step">
            <div className="vote-step-num">۴</div>
            <h4>دریافت محتوای آموزشی</h4>
            <p>
              پس از پردازش، لغات و اصطلاحات استخراج‌شده در پخش‌کننده در دسترس
              قرار می‌گیرد.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default VoteHome;